import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStoreGetters } from 'dashboard/composables/store';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';

// Keys that only widen conversation visibility; they never make anyone an
// administrator of a settings page.
const NON_ADMIN_KEYS = new Set([
  'administrator',
  'agent',
  'custom_role',
  'conversation_manage',
  'conversation_unassigned_manage',
  'conversation_participating_manage',
  'conversation_assigned_scope_only',
  'contact_manage',
  'report_manage',
  'knowledge_base_manage',
]);

/**
 * Composable to determine if the current user may administer the page they
 * are on: a real administrator, or an agent whose Teletel custom role carries
 * one of the management keys the current route declares in meta.permissions
 * (the server enforces the same key through PolicyExtensions / PermissionGuard).
 * @returns {Boolean} - True when the user may administer the current page.
 */
export function useAdmin() {
  const getters = useStoreGetters();
  const route = useRoute();

  const currentUserRole = computed(() => getters.getCurrentRole.value);
  const routeKeys = computed(() =>
    (route?.meta?.permissions || []).filter(key => !NON_ADMIN_KEYS.has(key))
  );
  const userPermissions = computed(() =>
    getUserPermissions(
      getters.getCurrentUser.value,
      getters.getCurrentAccountId.value
    )
  );
  const isAdmin = computed(
    () =>
      currentUserRole.value === 'administrator' ||
      routeKeys.value.some(key => userPermissions.value.includes(key))
  );

  return {
    isAdmin,
  };
}
